import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import pluckrLogo from '../assets/images/pluckr-logo.png';
import ThemeToggle from '@/components/ThemeToggle';
import { useThemeMode } from '@/hooks/useThemeMode';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { resolvedTheme } = useThemeMode();
  const isDark = resolvedTheme === 'dark';

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setIsLoading(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || (window.location.hostname === 'localhost' ? 'http://localhost:5000' : 'https://api.pluckr.ai');
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        toast.error(data.message || 'Request failed');
        setIsLoading(false);
        return;
      }

      setSubmitted(true);
      toast.success('Check your email for password reset instructions');
    } catch (error) {
      console.error('Forgot password error:', error);
      toast.error('Network error. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-background grid-pattern" data-testid="forgot-password-page">
      <Toaster
        position="top-right"
        theme={resolvedTheme}
        toastOptions={{
          style: {
            background: isDark ? '#282929' : '#ffffff',
            color: isDark ? '#ffffff' : '#1a1a1a',
            border: isDark ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid #e5e5e5',
          },
        }}
      />

      <ThemeToggle className="fixed top-4 right-4 z-[60]" />

      {/* Top-left brand */}
      <div className="px-8 pt-7">
        <Link
          to="/"
          className="inline-flex items-center gap-3 text-black/90 dark:text-white"
          style={{ fontFamily: "'Lora', Georgia, serif" }}
        >
          <img src={pluckrLogo} alt="Pluckr" className="h-9 w-auto" />
          <span className="text-[22px] font-bold tracking-[-0.01em]">Pluckr</span>
        </Link>
      </div>

      <div className="max-w-[1200px] mx-auto px-8 pb-16 pt-10">
        <div className="flex flex-col lg:flex-row items-center lg:items-start justify-between gap-14">
          {/* Left headline */}
          <div className="flex-1 w-full lg:pt-10">
            <h1
              className="text-[56px] sm:text-[72px] font-black leading-[0.95] tracking-[-0.03em]"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              <span className="block text-black/80 dark:text-white">Reset your</span>
              <span
                className="block"
                style={{ color: '#9be38f' }}
              >
                password
              </span>
            </h1>
          </div>

          {/* Right card */}
          <div className="w-full max-w-[420px]">
            <div
              className="bg-white dark:bg-[#282929] rounded-[18px] shadow-[0_20px_60px_rgba(0,0,0,0.25)] border border-black/5 dark:border-white/15 p-8"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {!submitted ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <p className="text-[14px] text-black/70 dark:text-white/70 mb-6">
                    Enter your email address and we'll send you a link to reset your password.
                  </p>

                  <div className="space-y-2">
                    <label className="text-[12px] font-medium text-black/70 dark:text-white/70">E-mail address</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full h-11 rounded-md border border-black/10 dark:border-white/20 bg-white dark:bg-[#282929] text-black dark:text-white px-3 text-[14px] outline-none focus:border-black/20 dark:focus:border-white/30"
                      placeholder=""
                      required
                      disabled={isLoading}
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full h-11 rounded-full font-semibold text-[13px] text-[#1a1a1a] transition-colors disabled:opacity-50 hover:brightness-95"
                    style={{ backgroundColor: 'var(--pluckr-green)' }}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Sending...' : 'Send reset link'}
                  </button>

                  <p className="text-[11px] text-black/45 dark:text-white/60 leading-snug pt-4">
                    Remember your password?{' '}
                    <Link
                      to="/early-access"
                      className="font-semibold text-black/75 dark:text-white hover:text-black dark:hover:text-white"
                    >
                      Back to login
                    </Link>
                  </p>
                </form>
              ) : (
                <div className="space-y-4 text-center">
                  <div style={{ fontSize: '40px' }}>📧</div>
                  <h2 className="text-[20px] font-semibold text-black/90 dark:text-white">Check your email</h2>
                  <p className="text-[14px] text-black/70 dark:text-white/70">
                    We've sent a password reset link to <strong>{email}</strong>
                  </p>
                  <p className="text-[12px] text-black/60 dark:text-white/70 mt-4">
                    The link will expire in 1 hour. If you don't see the email, check your spam folder.
                  </p>
                  <Link
                    to="/early-access"
                    className="inline-block mt-6 px-6 py-3 rounded-full font-semibold text-[13px] text-white transition-colors"
                    style={{ backgroundColor: '#1f2d2a' }}
                  >
                    Back to login
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
